/**
 * SCG SSO — env-driven URLs (must match backend `SSO_BASE_URL` + `SSO_APP_ID`).
 *
 * | Spec step | Behavior | Implementation |
 * |-----------|----------|------------------|
 * | 1 | Redirect to `{SSO_BASE}/{APP_ID}` | `getSsoEntryLoginUrl()` |
 * | 2 | SSO redirects to `/callback?code=` | `SsoCallbackPage` |
 * | 3 | `POST {SSO_BASE}/api/token` JSON `{ code }` | Browser → `POST {API}/api/sso/token` → proxy exchanges code, then server may call profile and return `user_email` / `user_display_name` / `profile` with the token |
 * | 4 | `GET {SSO_BASE}/api/profile/{APP_ID}` Bearer token | `GET {API}/api/sso/profile` → proxy (skipped when JWT carries email or local profile cache hits) |
 * | 5 | No / empty profile → SSO login redirect (default: `{SSO_BASE}/{APP_ID}`; optional `/api/login/...`) | `RequireAuth` + `getSsoLoginRedirectUrl()` when `isSsoEnforced()` |
 * | 6 | ADFS sign-out with `wreply` → `/post-logout`, then **`location.replace`** → `{SSO_BASE}/{APP_ID}` (always) | `logout()` + `PostLogoutRedirectPage` |
 */

const rawBase = (import.meta.env.VITE_SSO_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";
const appId = (import.meta.env.VITE_SSO_APP_ID as string | undefined)?.trim() ?? "";
const orgSlug = (import.meta.env.VITE_SSO_LOGIN_ORG_SLUG as string | undefined)?.trim() ?? "";

/**
 * True when `VITE_SSO_REQUIRED` is explicitly `"true"` (informational / legacy).
 * Actual gate for protected routes is {@link isSsoEnforced}.
 */
export const ssoRequired = import.meta.env.VITE_SSO_REQUIRED === "true";

/** True when SSO base + app id are set (entry URL works). Org slug is only required for optional API login path. */
export const ssoConfigured = Boolean(rawBase && appId);

const apiBaseTrim = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim() ?? "";

/**
 * When SSO is configured (`VITE_SSO_BASE_URL` + `VITE_SSO_APP_ID`) and `VITE_API_BASE_URL` is set,
 * Voice / Chat / History require a valid SSO session (see RequireAuth).
 * Set `VITE_SSO_REQUIRED=false` only for local development to bypass enforcement while SSO env vars remain set.
 */
export function isSsoEnforced(): boolean {
  if (import.meta.env.VITE_SSO_REQUIRED === "false") {
    return false;
  }
  return ssoConfigured && Boolean(apiBaseTrim);
}

/** Initial redirect (step 1): `{VITE_SSO_BASE_URL}/{VITE_SSO_APP_ID}` */
export function getSsoEntryLoginUrl(): string {
  return `${rawBase}/${appId}`;
}

/** `{VITE_SSO_BASE_URL}/api/login/{VITE_SSO_LOGIN_ORG_SLUG}/{VITE_SSO_APP_ID}` — use only with `VITE_SSO_LOGIN_USE_API_PATH=true` (some IdPs return 400 on this route). */
export function getSsoApiLoginUrl(): string {
  return `${rawBase}/api/login/${orgSlug}/${appId}`;
}

/**
 * “Please sign in” redirect (RequireAuth). Defaults to {@link getSsoEntryLoginUrl}.
 * Set `VITE_SSO_LOGIN_USE_API_PATH=true` to use {@link getSsoApiLoginUrl} (needs `VITE_SSO_LOGIN_ORG_SLUG`).
 */
export function getSsoLoginRedirectUrl(): string {
  const useApiPath = import.meta.env.VITE_SSO_LOGIN_USE_API_PATH === "true";
  if (useApiPath && rawBase && appId && orgSlug) {
    return getSsoApiLoginUrl();
  }
  return getSsoEntryLoginUrl();
}

/** ADFS (or IdP) post-logout URL — set `VITE_SSO_ADFS_LOGOUT_URL` in `.env` only (no fallbacks in code). */
export function getAdfsLogoutUrl(): string {
  return (import.meta.env.VITE_SSO_ADFS_LOGOUT_URL as string | undefined)?.trim() ?? "";
}

/** Same-origin path IdP returns to after ADFS sign-out; page then redirects to SSO entry in code. */
export const SSO_POST_LOGOUT_PATH = "/post-logout";

/** Full URL for ADFS `wreply` (browser only). */
export function getPostLogoutLandingUrl(): string {
  if (typeof window === "undefined" || !window.location?.origin) {
    return "";
  }
  return new URL(SSO_POST_LOGOUT_PATH, window.location.origin).href;
}

/**
 * Logout: open ADFS sign-out with `wreply` forced to this app’s {@link SSO_POST_LOGOUT_PATH}
 * (or SSO entry if origin unavailable). After IdP returns here, {@link PostLogoutRedirectPage}
 * runs `location.replace` → `{VITE_SSO_BASE_URL}/{VITE_SSO_APP_ID}`.
 */
export function getAdfsLogoutDestinationUrl(): string {
  return getAdfsLogoutUrlWithSsoReturn();
}

/**
 * ADFS URL with `wreply` always set to {@link getPostLogoutLandingUrl} when possible so our code
 * can redirect to SSO entry; overwrites any existing `wreply` query param.
 */
export function getAdfsLogoutUrlWithSsoReturn(): string {
  const adfs = getAdfsLogoutUrl().trim();
  if (!adfs) return "";
  const wreplyTarget = getPostLogoutLandingUrl().trim() || getSsoEntryLoginUrl().trim();
  if (!wreplyTarget) return adfs;
  try {
    const u = new URL(adfs);
    u.searchParams.set("wreply", wreplyTarget);
    return u.toString();
  } catch {
    const sep = adfs.includes("?") ? "&" : "?";
    return `${adfs}${sep}wreply=${encodeURIComponent(wreplyTarget)}`;
  }
}

/** One-shot: next `RequireAuth` mount skips `getUserDetails` after a topic-driven full page load (token must still exist). */
export const TOPIC_NAV_SKIP_AUTH_PROFILE_KEY = "__beluga_topic_nav_skip_auth_profile";

/** JSON profile cache keyed to the current access token — `VITE_SSO_PROFILE_CACHE_STORAGE_KEY` or default. */
export const SSO_PROFILE_CACHE_STORAGE_KEY =
  (import.meta.env.VITE_SSO_PROFILE_CACHE_STORAGE_KEY as string | undefined)?.trim() ||
  "beluga_sso_profile_cache_v1";

/**
 * `localStorage` key for SSO access token.
 * Override with `VITE_SSO_ACCESS_TOKEN_STORAGE_KEY` (e.g. per-environment). Default matches `.env` example.
 */
export const ACCESS_TOKEN_STORAGE_KEY =
  (import.meta.env.VITE_SSO_ACCESS_TOKEN_STORAGE_KEY as string | undefined)?.trim() ||
  "beluga_sso_access_token";

/**
 * `sessionStorage` key for post-login return path after `/callback`.
 * Override with `VITE_SSO_RETURN_PATH_STORAGE_KEY`.
 */
export const SSO_RETURN_PATH_KEY =
  (import.meta.env.VITE_SSO_RETURN_PATH_STORAGE_KEY as string | undefined)?.trim() ||
  "beluga_sso_return_path";
